import {
  FilterBarContainer,
  FilterBarLeftContent,
  SearchFieldWrapper,
} from "./FilterBar.styled";
import { Select, Button, TextField } from "@ann/web-components";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import useDebounce from "../../../../../hooks/useDebounce";
import { US_CITY_OPTIONS } from "../../constants/usCities";

const FilterBar = ({
  searchValue,
  onSearchChange,
  selectedCity,
  onCityChange,
  onClearFilters,
  cityOptions = US_CITY_OPTIONS,
}) => {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue || "");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [selectInstanceKey, setSelectInstanceKey] = useState(0);
  const searchTimeoutRef = useRef(null);

  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 700);

  // Sync local state with prop when it changes
  useEffect(() => {
    setLocalSearchValue(searchValue || "");
  }, [searchValue]);

  const filteredCities = useMemo(() => {
    if (!debouncedCitySearchTerm.trim()) {
      return cityOptions;
    }

    const searchLower = debouncedCitySearchTerm.toLowerCase();
    return cityOptions.filter(({ label }) =>
      label.toLowerCase().includes(searchLower)
    );
  }, [cityOptions, debouncedCitySearchTerm]);

  const handleSearchInputChange = useCallback(
    (value) => {
      setLocalSearchValue(value);
      
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Set new timeout for 500ms debounce
      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange?.(value);
      }, 500);
    },
    [onSearchChange]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleCitySelection = useCallback(
    (value) => {
      onCityChange?.(value ?? null);
    },
    [onCityChange]
  );

  const handleClearAllFilters = useCallback(() => {
    setLocalSearchValue("");
    setCitySearchTerm("");
    setSelectInstanceKey((prev) => prev + 1);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    onSearchChange?.("");
    onClearFilters?.();
  }, [onSearchChange, onClearFilters]);

  return (
    <FilterBarContainer>
      <FilterBarLeftContent>
        <SearchFieldWrapper>
          <TextField
            iconLeft="search"
            onChange={handleSearchInputChange}
            size="small"
            placeholder="Search Contacts..."
            value={localSearchValue}
          />
        </SearchFieldWrapper>
        <Select
          key={`city-select-${selectInstanceKey}`}
          label="City"
          placeholder="City"
          value={selectedCity ?? undefined}
          onChange={handleCitySelection}
          size="small"
          width="auto"
          minWidth="120px"
        >
          <Select.Menu width="200px" >
            <Select.Search
              placeholder="Search a city"
              onChange={setCitySearchTerm}
              label=""
            />
            {filteredCities.map(({ label, value }) => (
              <Select.Item key={value} value={value} label={label}>
                {label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        <Button
          label="Clear Filters"
          size="medium"
          type="secondary"
          onClick={handleClearAllFilters}
        />
      </FilterBarLeftContent>
    </FilterBarContainer>
  );
};

export default FilterBar;
